import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, LogOut, CheckCircle, XCircle, Bot } from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import EmptyState from '../../components/EmptyState';
import { AIBadge } from '../../components/AIComponents';

const clientSubNav = [
  { path: '/client', label: 'Dashboard' },
  { path: '/client/quotes', label: 'Quotes' },
  { path: '/client/invoices', label: 'Invoices' },
];

const mockQuotes = [
  { id: 234, title: 'Garden Landscaping', amount: 4140, status: 'pending', date: '2024-01-12', validUntil: '2024-02-12' },
  { id: 230, title: 'Bathroom Renovation', amount: 5040, status: 'accepted', date: '2024-01-05' },
];

// Client AI Help Component
function ClientAIHelp() {
  return (
    <div className="client-ai-help">
      <div className="client-ai-help-icon">
        <Bot size={20} />
      </div>
      <div className="client-ai-help-content">
        <h4>Need help understanding this?</h4>
        <p>AI explains only. It cannot accept or change anything.</p>
      </div>
      <button disabled title="AI explains only. It cannot accept or change anything.">
        Ask AI to explain
      </button>
    </div>
  );
}

export function ClientQuotes() {
  const location = useLocation();

  return (
    <div className="client-portal">
      <header className="client-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <h1 style={{ fontFamily: 'var(--font-family-display)', fontSize: '1.25rem', fontWeight: '700' }}>
            Trade<span style={{ color: 'var(--accent-primary)' }}>Life</span>
          </h1>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Client Portal</span>
        </div>
        <button className="btn btn-ghost btn-icon" style={{ color: 'var(--text-muted)' }}>
          <LogOut size={18} />
        </button>
      </header>

      <div className="client-content">
        <div className="client-nav">
          {clientSubNav.map((item) => (
            <Link key={item.path} to={item.path} className={`client-nav-link ${location.pathname === item.path ? 'active' : ''}`}>
              {item.label}
            </Link>
          ))}
        </div>

        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-lg)' }}>
          Your Quotes
        </h2>

        <GlassPanel title="All Quotes" titleIcon={FileText}>
          {mockQuotes.length > 0 ? (
            <div className="list-container">
              {mockQuotes.map((quote) => (
                <div key={quote.id} className="glass-panel" style={{ marginBottom: 'var(--space-md)', cursor: 'default' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)' }}>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        Quote #{quote.id}
                        <AIBadge text="AI Help" />
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{quote.title}</p>
                    </div>
                    <span className={`status-badge ${quote.status === 'accepted' ? 'completed' : 'pending'}`}>
                      {quote.status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-lg)' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Date</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{quote.date}</div>
                      </div>
                      {quote.validUntil && (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Valid Until</div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{quote.validUntil}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--accent-primary)' }}>
                      £{quote.amount.toLocaleString()}
                    </div>
                  </div>
                  {quote.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 'var(--space-md)', marginTop: 'var(--space-lg)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--glass-border)' }}>
                      <button className="btn btn-primary" disabled style={{ flex: 1 }}>
                        <CheckCircle size={16} />Accept Quote
                      </button>
                      <button className="btn btn-secondary" disabled style={{ flex: 1 }}>
                        <XCircle size={16} />Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={FileText} title="No quotes" description="You don't have any quotes yet" />
          )}
        </GlassPanel>

        {/* Client AI Help */}
        <ClientAIHelp />
      </div>
    </div>
  );
}

export default ClientQuotes;
